import { collection, addDoc, updateDoc, deleteDoc, serverTimestamp, onSnapshot, query, where, orderBy, limit, getDoc, doc, getDocs, Timestamp, Unsubscribe } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { CourseRating } from '../types/course';

// Helper function to extract username from email address for privacy
export const extractUserNameFromEmail = (email: string | null | undefined): string => {
  if (!email) return 'Käyttäjä';
  // If it's an email address, extract the part before @
  if (email.includes('@')) {
    return email.split('@')[0];
  }
  return email;
};

/**
 * Service class for managing course ratings
 * This centralizes all rating-related functionality in one place
 */
export class CourseRatingService {
  private static instance: CourseRatingService;
  private ratingListeners: Map<string, Unsubscribe> = new Map();

  private constructor() {}

  /**
   * Get the singleton instance of CourseRatingService
   */
  public static getInstance(): CourseRatingService {
    if (!CourseRatingService.instance) {
      CourseRatingService.instance = new CourseRatingService();
    }
    return CourseRatingService.instance;
  }

  /**
   * Listen for ratings for a specific course
   * @param courseId The course ID to listen for ratings
   * @param callback Function to call when ratings change
   * @param limit Number of ratings to fetch (default: 5)
   * @returns Unsubscribe function to stop listening
   */
  public listenForCourseRatings(
    courseId: string, 
    callback: (ratings: CourseRating[]) => void,
    ratingLimit: number = 5
  ): Unsubscribe {
    // Remove any existing listener for this course
    this.stopListeningForCourseRatings(courseId);
    
    // Create a query for this course's ratings
    const ratingsQuery = query(
      collection(db, 'courseRatings'),
      where('courseId', '==', courseId),
      orderBy('timestamp', 'desc'),
      limit(ratingLimit)
    );
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(ratingsQuery, async (snapshot) => {
      if (!snapshot.empty) {
        // Process initial ratings data
        const ratingsWithBasicInfo = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            rating: data.rating,
            comment: data.comment,
            timestamp: data.timestamp?.toDate() || new Date(),
            userId: data.userId,
            userName: data.userName || 'Käyttäjä',
            courseId: data.courseId
          } as CourseRating;
        });
        
        // Now fetch user information for each rating
        const ratingsWithUserInfo = await Promise.all(
          ratingsWithBasicInfo.map(async (rating) => {
            // If we already have userName, use it
            if (rating.userName && rating.userName !== 'Käyttäjä') {
              return rating;
            }
            
            // Otherwise try to fetch from users collection
            if (rating.userId) {
              try {
                const userDocRef = doc(db, 'users', rating.userId);
                const userDoc = await getDoc(userDocRef);
                
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    ...rating,
                    userName: userData.displayName || extractUserNameFromEmail(userData.email) || 'Käyttäjä'
                  };
                }
              } catch (error) {
                // Error silently handled
              }
            }
            
            return rating;
          })
        );
        
        // Call callback with complete user information
        callback(ratingsWithUserInfo);
      } else {
        callback([]);
      }
    });
    
    // Store the unsubscribe function for later cleanup
    this.ratingListeners.set(courseId, unsubscribe);
    
    return unsubscribe;
  }

  /**
   * Stop listening for a specific course's ratings
   * @param courseId The course ID to stop listening for
   */
  public stopListeningForCourseRatings(courseId: string): void {
    const unsubscribe = this.ratingListeners.get(courseId);
    if (unsubscribe) {
      unsubscribe();
      this.ratingListeners.delete(courseId);
    }
  }

  /**
   * Stop listening for all course ratings
   */
  public stopAllRatingListeners(): void {
    this.ratingListeners.forEach(unsubscribe => unsubscribe());
    this.ratingListeners.clear();
  }

  /**
   * Submit a rating for a course
   * @param courseId The course ID to rate
   * @param rating The rating (green, yellow, or red)
   * @param comment Optional comment for the rating
   * @param userId The user ID submitting the rating
   * @param userName The user's display name or username
   * @returns Promise resolving to success or error message
   */
  public async submitRating(
    courseId: string,
    rating: 'green' | 'yellow' | 'red',
    comment: string,
    userId: string,
    userName: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Create a timestamp for 24 hours ago
      const oneDayAgo = new Date();
      oneDayAgo.setHours(oneDayAgo.getHours() - 24);
      
      // Check if the user has already rated this course in the last 24 hours
      const recentRatingsQuery = query(
        collection(db, 'courseRatings'),
        where('courseId', '==', courseId),
        where('userId', '==', userId),
        where('timestamp', '>', Timestamp.fromDate(oneDayAgo)),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      
      const recentRatingsSnapshot = await getDocs(recentRatingsQuery);
      
      // Prepare the rating data
      const ratingData = {
        comment: comment,
        courseId: courseId,
        rating: rating,
        timestamp: serverTimestamp(),
        userId: userId,
        userName: userName
      };
      
      // If the user has already rated this course in the last 24 hours, update that rating
      if (!recentRatingsSnapshot.empty) {
        const existingRatingDoc = recentRatingsSnapshot.docs[0];
        await updateDoc(doc(db, 'courseRatings', existingRatingDoc.id), ratingData);
        return { 
          success: true, 
          message: 'Aiempi arviosi päivitettiin! Kiitos palautteesta.'
        };
      } else {
        // Otherwise, create a new rating
        await addDoc(collection(db, 'courseRatings'), ratingData);
        return { 
          success: true,
          message: 'Arviosi on tallennettu! Kiitos palautteesta.'
        };
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      return {
        success: false,
        message: 'Tallennus epäonnistui. Yritä uudelleen.'
      };
    }
  }

  /**
   * Delete a rating by ID
   * @param ratingId The rating ID to delete
   * @returns Promise resolving to success or error message
   */
  public async deleteRating(ratingId: string): Promise<{ success: boolean; message: string }> {
    try {
      await deleteDoc(doc(db, 'courseRatings', ratingId));
      
      return {
        success: true,
        message: 'Arvio poistettu onnistuneesti!'
      };
    } catch (error) {
      console.error("Error deleting rating:", error);
      return {
        success: false,
        message: 'Arvion poistaminen epäonnistui. Yritä uudelleen.'
      };
    }
  }

  /**
   * Calculate the average rating for a course
   * Green = 1, Yellow = 0, Red = -1 for weighted average
   * @param ratings Array of ratings to calculate average from
   * @returns Score between -1 and 1
   */
  public calculateAverageRating(ratings: CourseRating[]): number {
    if (ratings.length === 0) return 0;
    
    const weightMap = {
      'green': 1,
      'yellow': 0,
      'red': -1
    };
    
    const sum = ratings.reduce((acc, rating) => {
      return acc + weightMap[rating.rating as keyof typeof weightMap];
    }, 0);
    
    return sum / ratings.length;
  }
}
